/**
 * Painel de validação de preço (Rent_Bud_2026) — snapshot `pnl_audit_snapshot`.
 *
 * O painel reflecte directamente o ``pnl_audit_snapshot`` persistido na
 * cotação. O recálculo é desencadeado pelo auto-save da cotação no
 * RFQDetailPage (debounce ~1 s); este componente limita-se a apresentar
 * o snapshot mais recente que recebe via prop.
 */
import { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Info,
  MinusCircle,
} from 'lucide-react';
import { t } from '@/i18n/translations';
import { CollapsibleCard } from '@/components/ui/collapsible-card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const TRAFFIC = {
  VERDE: { color: 'bg-emerald-100 text-emerald-900 border-emerald-300', Icon: CheckCircle2, iconClass: 'text-emerald-600' },
  AMARELO: { color: 'bg-amber-100 text-amber-900 border-amber-300', Icon: AlertTriangle, iconClass: 'text-amber-600' },
  VERMELHO: { color: 'bg-red-100 text-red-900 border-red-300', Icon: MinusCircle, iconClass: 'text-red-600' },
};

import { fmtIntSpaced, fmtEur, fmtEurExact, fmtNum, fmtPct } from '@/lib/pnlFormat';

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between px-2 py-1.5 text-sm last:pb-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}

/**
 * Substituição numérica de uma fórmula. Devolve ``null`` se algum operando
 * faltar — snapshots gravados antes de Ago 2026 não trazem todos os campos,
 * e nesse caso mostramos só a fórmula simbólica.
 */
function subst(values, build) {
  if (values.some((v) => v === null || v === undefined || Number.isNaN(Number(v)))) {
    return null;
  }
  return build(...values.map((v) => fmtNum(v)));
}

function AuxRow({ label, formula, sub, value }) {
  return (
    <div className="px-3 py-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-medium">{label}</span>
        <span className="shrink-0 text-sm font-semibold tabular-nums">{value}</span>
      </div>
      <div className="mt-0.5 break-words font-mono text-[11px] leading-snug text-muted-foreground">
        {formula}
        {sub ? ` = ${sub}` : null}
      </div>
    </div>
  );
}

function AuxGroup({ title, rows }) {
  return (
    <div className="rounded-md bg-muted/40">
      <div className="sub-section-label px-3 py-1.5">
        {title}
      </div>
      {rows.map((r) => (
        <AuxRow key={r.label} {...r} />
      ))}
    </div>
  );
}

/** Memória de cálculo do card: cada valor com a fórmula e os operandos. */
function AuxCalculations({ snapshot, lang }) {
  const [open, setOpen] = useState(false);
  const inp = snapshot.inputs || {};
  const inter = snapshot.intermediate || {};
  const res = snapshot.results || {};
  const cs = snapshot.cost_structure_snapshot || {};
  const th = snapshot.thresholds || {};

  const fixedLines = Array.isArray(cs.fixed_lines) ? cs.fixed_lines : [];
  const fixedSub = fixedLines.length
    ? fixedLines.map((l) => fmtNum(l.annual_amount_eur, 2)).join(' + ')
    : null;

  const groups = [
    {
      title: t('pv_aux_group_quantity', lang),
      rows: [
        {
          label: t('pv_aux_cadence', lang),
          formula: t('pv_aux_cadence_f', lang),
          sub: subst(
            [inp.shots_per_hour, inp.trs_percent, inp.num_cavities],
            (a, b, c) => `${a} × ${b} × ${c}`,
          ),
          value: fmtNum(inp.cadence_pcs_hr),
        },
        {
          label: t('pv_aux_hours_year', lang),
          formula: t('pv_aux_hours_year_f', lang),
          sub: subst(
            [
              cs.working_days_per_year,
              cs.shifts_per_day_injection,
              cs.hours_per_turn_injection,
              cs.injection_machine_count,
              cs.capacity_utilization_rate,
              cs.macro_scrap_rate,
            ],
            (d, s, h, m, u, k) => `${d} × ${s} × ${h} × ${m} × ${u} × (1 − ${k})`,
          ),
          value: fmtNum(inp.hours_injection_year),
        },
        {
          label: t('qtd_pieces_year', lang),
          formula: t('pv_aux_qtd_f', lang),
          sub: subst(
            [inp.hours_injection_year, inp.cadence_pcs_hr],
            (h, c) => `${h} × ${c}`,
          ),
          value: fmtNum(inp.qtd_pieces_year),
        },
      ],
    },
    {
      title: t('price_validation_support', lang),
      rows: [
        {
          label: t('ca_vendu', lang),
          formula: t('pv_aux_ca_f', lang),
          sub: subst([inp.qtd_pieces_year, inp.pv_proposed_eur], (q, p) => `${q} × ${p}`),
          value: fmtEurExact(inter.ca_vendu_eur),
        },
        {
          label: t('va_vendu', lang),
          formula: t('pv_aux_va_vendu_f', lang),
          sub: subst(
            [inp.pv_proposed_eur, inp.vm_per_piece_eur, inp.qtd_pieces_year],
            (p, v, q) => `(${p} − ${v}) × ${q}`,
          ),
          value: fmtEurExact(inter.va_vendu_eur),
        },
        {
          label: t('tonnes', lang),
          formula: t('pv_aux_tonnes_f', lang),
          sub: subst(
            [inp.qtd_pieces_year, inp.piece_weight_kg],
            (q, w) => `${q} × ${w} ÷ 1000`,
          ),
          value: inter.tonnes != null ? `${fmtNum(inter.tonnes)} t` : '—',
        },
      ],
    },
    {
      title: t('resultado_va', lang),
      rows: [
        {
          label: t('pv_aux_energy', lang),
          formula: t('pv_aux_energy_f', lang),
          sub: subst(
            [inp.qtd_pieces_year, inp.piece_weight_kg, inp.energy_eur_per_kg_used],
            (q, w, e) => `${q} × ${w} × ${e}`,
          ),
          value: fmtEurExact(inter.energy_cost_eur),
        },
        {
          label: t('pv_aux_variable', lang),
          formula: t('pv_aux_variable_f', lang),
          sub: subst(
            [inter.va_vendu_eur, inter.sum_variable_pct],
            (v, p) => `${v} × ${p}`,
          ),
          value: fmtEurExact(inter.variable_on_va_eur),
        },
        {
          label: t('pv_aux_fixed', lang),
          formula: t('pv_aux_fixed_f', lang),
          sub: fixedSub,
          value: fmtEurExact(inter.sum_fixed_costs_eur),
        },
        {
          label: t('pv_aux_finishing_seconds', lang),
          formula: t('pv_aux_finishing_seconds_f', lang),
          sub: null,
          value:
            inter.finishing_seconds_per_part != null
              ? `${fmtNum(inter.finishing_seconds_per_part)} s`
              : '—',
        },
        {
          label: t('pv_aux_effectif', lang),
          formula: t('pv_aux_effectif_f', lang),
          sub: subst(
            [
              inp.qtd_pieces_year,
              inter.finishing_seconds_per_part,
              cs.working_days_per_year,
              cs.hours_per_turn_finishing,
            ],
            (q, s, d, h) => `${q} × (${s} ÷ 3600) ÷ (${d} × ${h})`,
          ),
          value: fmtNum(inter.effectif_finishing_fte),
        },
        {
          label: t('pv_aux_mod', lang),
          formula: t('pv_aux_mod_f', lang),
          sub: subst(
            [inter.effectif_finishing_fte, cs.finishing_staff_cost_eur_per_fte_year],
            (e, c) => `${e} × ${c}`,
          ),
          value: fmtEurExact(inter.mod_finishing_cost_eur),
        },
        {
          label: t('pv_aux_transport', lang),
          formula: t('pv_aux_transport_f', lang),
          sub: subst(
            [inp.qtd_pieces_year, inp.transport_eur_per_piece],
            (q, tr) => `${q} × ${tr}`,
          ),
          value: fmtEurExact(inter.transport_year_eur),
        },
        {
          label: t('resultado_va', lang),
          formula: t('pv_aux_resultado_va_f', lang),
          sub: subst(
            [
              inter.va_vendu_eur,
              inter.energy_cost_eur,
              inter.variable_on_va_eur,
              inter.sum_fixed_costs_eur,
              inter.mod_finishing_cost_eur,
              inter.transport_year_eur,
            ],
            (va, e, v, f, m, tr) => `${va} − (${e} + ${v} + ${f} + ${m} + ${tr})`,
          ),
          value: fmtEurExact(res.resultado_va_eur),
        },
      ],
    },
    {
      title: t('resultado_vm', lang),
      rows: [
        {
          label: t('pv_aux_vm_sold', lang),
          formula: t('pv_aux_vm_sold_f', lang),
          sub: subst(
            [inp.qtd_pieces_year, inp.vm_per_piece_eur],
            (q, v) => `${q} × ${v}`,
          ),
          value: fmtEurExact(inter.vm_vendida_eur),
        },
        {
          label: t('pv_aux_vm_bought', lang),
          formula: t('pv_aux_vm_bought_f', lang),
          sub: subst(
            [
              inter.tonnes,
              inp.fire_loss_resale_rate,
              inp.base_mb_eur_per_t,
              inp.alloy_buy_premium_eur_per_t,
            ],
            (tn, fl, base, prem) => `${tn} × (1 + ${fl}) × (${base} + ${prem})`,
          ),
          value: fmtEurExact(inter.vm_comprada_eur),
        },
        {
          label: t('pv_aux_vm_paf_resold', lang),
          formula: t('pv_aux_vm_paf_resold_f', lang),
          sub: subst(
            [
              inp.fire_loss_resale_rate,
              inter.tonnes,
              inp.slag_valuation_rate,
              inp.base_mb_eur_per_t,
              inp.slag_share_rate,
              inp.chips_valuation_rate,
              inp.chips_share_rate,
            ],
            (fl, tn, sv, base, ss, cv, cs2) =>
              `${fl} × ${tn} × (${sv} × ${base} × ${ss} + ${cv} × ${base} × ${cs2})`,
          ),
          value: fmtEurExact(inter.vm_paf_resold_eur),
        },
        {
          label: t('resultado_vm', lang),
          formula: t('pv_aux_resultado_vm_f', lang),
          sub: subst(
            [inter.vm_vendida_eur, inter.vm_comprada_eur, inter.vm_paf_resold_eur],
            (a, b, c) => `${a} − ${b} + ${c}`,
          ),
          value: fmtEurExact(res.resultado_vm_eur),
        },
      ],
    },
    {
      title: t('pv_aux_group_margin', lang),
      rows: [
        {
          label: t('marge_total_eur', lang),
          formula: t('pv_aux_marge_f', lang),
          sub: subst(
            [res.resultado_va_eur, res.resultado_vm_eur],
            (a, b) => `${a} + ${b}`,
          ),
          value: fmtEurExact(res.marge_vm_va_eur),
        },
        {
          label: t('kpi_marge_vm_va_pct', lang),
          formula: t('pv_aux_marge_pct_f', lang),
          sub: subst(
            [res.marge_vm_va_eur, inter.ca_vendu_eur],
            (m, ca) => `${m} ÷ ${ca}`,
          ),
          value: fmtPct(res.marge_vm_va_pct),
        },
        {
          label: t('pv_aux_thresholds', lang),
          formula: `${t('traffic_VERDE', lang)} ≥ ${fmtPct(th.green_min_pct)} · ${t('traffic_AMARELO', lang)} ≥ ${fmtPct(th.warn_min_pct)}`,
          sub: null,
          value: t(`traffic_${snapshot.verdict_traffic || 'VERMELHO'}`, lang),
        },
      ],
    },
  ];

  return (
    <div className="rounded-md bg-muted/40">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium uppercase tracking-wide hover:bg-muted/50"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-150 ${open ? '' : '-rotate-90'}`}
          aria-hidden="true"
        />
        {t('pv_aux_title', lang)}
      </button>
      {open ? (
        <div className="space-y-2 px-2 pb-2">
          {groups.map((g) => (
            <AuxGroup key={g.title} title={g.title} rows={g.rows} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function LegacySnapshotNotice({ lang }) {
  return (
    <Alert>
      <Info className="h-4 w-4" />
      <AlertTitle>{t('pnl_legacy_snapshot_title', lang)}</AlertTitle>
      <AlertDescription className="text-sm">{t('pnl_legacy_snapshot_hint', lang)}</AlertDescription>
    </Alert>
  );
}

export default function PnLAuditPanel({ snapshot, lang = 'pt' }) {
  const isRentBud = snapshot && snapshot.schema_version === 'rent_bud_2026';

  if (!snapshot) {
    return (
      <CollapsibleCard id="pnl_audit" title={t('price_validation_title', lang)}>
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>{t('pnl_audit_unavailable', lang)}</AlertTitle>
          <AlertDescription className="text-sm">{t('pnl_audit_unavailable_hint', lang)}</AlertDescription>
        </Alert>
      </CollapsibleCard>
    );
  }

  if (!isRentBud) {
    return (
      <CollapsibleCard id="pnl_audit" title={t('price_validation_title', lang)}>
        <LegacySnapshotNotice lang={lang} />
      </CollapsibleCard>
    );
  }

  const trafficKey = snapshot.verdict_traffic || 'VERMELHO';
  const tc = TRAFFIC[trafficKey] || TRAFFIC.VERMELHO;
  const Icon = tc.Icon;
  const res = snapshot.results || {};
  const inter = snapshot.intermediate || {};
  const warnings = Array.isArray(snapshot.warnings) ? snapshot.warnings : [];

  return (
    <CollapsibleCard
      id="pnl_audit"
      title={t('price_validation_title', lang)}
      description={t('price_validation_subtitle', lang)}
      contentClassName="space-y-4"
      headerExtra={(
        <div className={`flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium ${tc.color}`}>
          <Icon className={`h-4 w-4 ${tc.iconClass}`} />
          <span>{t(`traffic_${trafficKey}`, lang)}</span>
        </div>
      )}
    >
        <div className="rounded-lg bg-muted/50 p-4 text-center">
          <div className="sub-section-label">{t('kpi_marge_vm_va_pct', lang)}</div>
          <div className="mt-1 text-3xl font-bold tabular-nums">{fmtPct(res.marge_vm_va_pct)}</div>
          <Badge variant="outline" className="mt-2">{snapshot.verdict}</Badge>
        </div>

        <div className="rounded-md bg-muted/40 px-1 py-1">
          <div className="sub-section-label px-2 py-2">
            {t('price_validation_decomposition', lang)}
          </div>
          <Row label={t('resultado_va', lang)} value={fmtEur(res.resultado_va_eur)} />
          <Row label={t('resultado_vm', lang)} value={fmtEur(res.resultado_vm_eur)} />
          <Row label={t('marge_total_eur', lang)} value={fmtEur(res.marge_vm_va_eur)} />
        </div>

        <div className="rounded-md bg-muted/40 px-1 py-1">
          <div className="sub-section-label px-2 py-2">
            {t('price_validation_support', lang)}
          </div>
          <Row
            label={t('qtd_pieces_year', lang)}
            value={
              snapshot.inputs?.qtd_pieces_year != null
                ? fmtIntSpaced(snapshot.inputs.qtd_pieces_year)
                : '—'
            }
          />
          <Row label={t('ca_vendu', lang)} value={fmtEur(inter.ca_vendu_eur)} />
          <Row label={t('va_vendu', lang)} value={fmtEur(inter.va_vendu_eur)} />
          <Row label={t('tonnes', lang)} value={inter.tonnes != null ? `${Number(inter.tonnes).toFixed(4)} t` : '—'} />
        </div>

        {warnings.length > 0 ? (
          <Alert variant="default">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('pnl_warnings', lang)}</AlertTitle>
            <AlertDescription>
              <ul className="mt-1 list-disc pl-5 text-sm">
                {warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        ) : null}

        <AuxCalculations snapshot={snapshot} lang={lang} />
    </CollapsibleCard>
  );
}
